/**
 * CMS Channel Pages Query
 *
 * Server-side query for fetching channel page data.
 * Handles both sender (full station pages) and stream (minimal pages).
 */

import { query } from "@solidjs/router";
import type { CMSChannelPage, CMSChannelPageResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { buildChannelPageByIdentifierUrl } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant } from "../queryUtils";

/**
 * Server-side query for fetching a channel page by identifier
 *
 * Works for both sender and stream pages. The pageType is auto-derived
 * from the linkedChannel.channelType on the CMS side.
 *
 * NOTE: Uses depth=2 (linkedChannel with branding) instead of depth=5.
 * StationShowsBlockRenderer and StationHostsBlockRenderer fetch their own
 * data for better performance (parallel requests, deferred streaming).
 *
 * @param identifier - The channel identifier (e.g., 'hot', 'relax', 'music-xl')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Channel page data or null if not found
 */
export const getChannelPageByIdentifierQuery = query(
  async (identifier: string, tenantSlug: string, previewToken?: string) => {
    "use server";
    const draft = isPreviewTokenValid(previewToken, tenantSlug);

    const LOG_PREFIX = "[channelPageByIdentifier]";

    const url = `${env.cmsApiUrl}${buildChannelPageByIdentifierUrl({ identifier, tenantSlug, draft })}`;

    const data = await fetchFromCMS<CMSChannelPageResponse>(url, {
      logPrefix: LOG_PREFIX,
      previewToken: draft ? previewToken : undefined,
    });

    if (!data?.docs || data.docs.length === 0) {
      logger.warn(`${LOG_PREFIX} No channel page found with identifier: ${identifier}`);
      return null;
    }

    // Payload ignores nested tenant[slug] filters — filter client-side
    const channelPage = findByTenant(data.docs, tenantSlug);

    if (!channelPage) {
      logger.warn(`${LOG_PREFIX} No channel page found for tenant: ${tenantSlug}`);
      return null;
    }

    // Derive pageType from linkedChannel if not set
    if (!channelPage.pageType && channelPage.linkedChannel) {
      const linkedChannel =
        typeof channelPage.linkedChannel === "object" ? channelPage.linkedChannel : null;
      channelPage.pageType = linkedChannel?.channelType || "sender";
    }

    logger.info(
      `${LOG_PREFIX} Found channel page: ${channelPage.title} (type: ${channelPage.pageType})`
    );
    return channelPage as CMSChannelPage;
  },
  "channelPageByIdentifier"
);
